import { type FC, useCallback, useState } from 'react';

import { MarkdownText } from '@vakantiesnl/components/src/__LEGACY__/atoms/MarkdownText';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { ReportButton } from '@vakantiesnl/components/src/atoms/ReportButton';
import { Tag } from '@vakantiesnl/components/src/atoms/Tag';
import { RatingBox } from '@vakantiesnl/components/src/molecules/RatingBox';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { getFormatedFullMonthDate } from '@vakantiesnl/services/src/util/dateUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { type Reviews, type Search } from '@vakantiesnl/types';

import { useCustomStyles } from './ReviewItem.style';

export type ReviewItemProps = {
	review: Reviews.ReviewView;
	accommodation?: Search.CompactAccommodationItem;
};

export const ReviewItem: FC<ReviewItemProps> = ({ review, accommodation }) => {
	const { classes: styles } = useCustomStyles();
	const { isMobile } = useBreakpoints();
	const microCopies = useMicroCopyContext();
	const [commentsOpen, setCommentsOpen] = useState(false);

	const toggleComments = useCallback(() => setCommentsOpen((open) => !open), []);

	return (
		<div className={styles.container} data-cy="review-item">
			<div className={styles.header}>
				<div className={styles.headerLeftSection}>
					<RatingBox className={styles.ratingBox} rating={review.score} size="medium" label={false}>
						<div className={styles.titleContainer}>
							<div className={styles.title}>{review.title}</div>
							<div className={styles.authorAndDate}>
								{inject(microCopies['review.authorAndDateTemplate'], {
									userName: review.userName,
									date: getFormatedFullMonthDate(new Date(review.entryDate), 'DD-MM-YYYY'),
								})}
							</div>
						</div>
					</RatingBox>
					{isMobile && (
						<ReportButton
							title={microCopies['reviews.report']}
							iconOnly={true}
							type="review"
							review={review}
							accommodation={accommodation}
						/>
					)}
				</div>
				<div className={styles.headerRightSection}>
					<Tag size="small" color="purple" leadingIcon="familyRoom">
						{microCopies[`review.travelWidth.${review.traveledWith}`]}
					</Tag>
					{!isMobile && (
						<ReportButton
							title={microCopies['reviews.report']}
							iconOnly={true}
							type="review"
							review={review}
							accommodation={accommodation}
						/>
					)}
				</div>
			</div>
			<MarkdownText className={styles.body} content={review.text ?? ''} />
			<div className={styles.actions}>
				{/*TODO: like buttons to be added here*/}
				{!!review.comments?.length && (
					<Button variant="secondary" leadingIcon="chat" onClick={toggleComments}>
						{review.comments.length}
					</Button>
				)}
			</div>
			{!!review.comments?.length && commentsOpen && (
				<>
					<div className={styles.divider} />
					{review.comments.map((comment, index) => (
						<div key={index} className={styles.comment}>
							<div className={styles.commentAuthor}>
								{!comment.author
									? microCopies['reviews.reactionAuthorDefault']
									: comment.author.position &&
										(comment.author.position === 'Owner'
											? microCopies['review.reaction.ownerPosition']
											: comment.author.position)}
							</div>
							<MarkdownText className={styles.body} content={comment.content.replace(/\*/g, '\\*')} />
						</div>
					))}
				</>
			)}
		</div>
	);
};
